import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Helper function to handle navigation and scroll behavior
  const handleNavClick = (e, path) => {
    // Only apply special handling for non-hash links
    if (!path.includes('#')) {
      window.scrollTo(0, 0);
    }
    
    // Close mobile menu if open
    setIsOpen(false);
  };

  const navItems = [
    { name: 'Services', href: '/services' },
    { name: 'Free AI Audit', href: '/free-ai-audit' },
    { name: 'Why Us', href: '/#why-choose-us' },
    { name: 'Our Apps', href: '/services/ai-web-applications#golfai-guru', target: '_self' },
    { name: 'AI Agents', href: '/#ai-agents' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact', href: '/#contact' },
  ];

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${
      scrolled ? 'bg-white/80 backdrop-blur-md shadow-sm' : 'bg-transparent'
    }`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center"
          >
            <Link 
              to="/" 
              className="flex items-center space-x-2 group"
              onClick={(e) => handleNavClick(e, "/")}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-[#FF7066] via-accent to-secondary blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300" />
                <span className="relative text-4xl font-bold font-bytesized tracking-tight bg-gradient-to-r from-[#FF7066] via-accent to-secondary bg-clip-text text-transparent group-hover:from-secondary group-hover:via-[#FF7066] group-hover:to-accent transition-all duration-300">
                  BetterByAI
                </span>
              </div>
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                {item.href.startsWith('/') && !item.href.includes('#') ? (
                  <Link 
                    to={item.href}
                    onClick={(e) => handleNavClick(e, item.href)}
                    className={`text-sm font-medium font-inter tracking-wide transition-colors duration-200 ${
                      scrolled ? 'text-secondary hover:text-primary' : 'text-secondary hover:text-primary'
                    }`}
                  >
                    {item.name}
                  </Link>
                ) : (
                  <a 
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.href)}
                    className={`text-sm font-medium font-inter tracking-wide transition-colors duration-200 ${
                      scrolled ? 'text-secondary hover:text-primary' : 'text-secondary hover:text-primary'
                    }`}
                  >
                    {item.name}
                  </a>
                )}
              </motion.div>
            ))}
            <motion.a
              href="https://calendly.com/amrit-betterbyai/30min"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-[#FF7066] hover:bg-[#FF8A82] text-white font-medium px-4 py-2 rounded-md transition-colors"
            >
              Book a Meeting
            </motion.a>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-secondary hover:text-primary focus:outline-none"
            >
              {isOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white shadow-lg"
          >
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navItems.map((item) => (
                item.href.startsWith('/') && !item.href.includes('#') ? (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="block px-3 py-2 text-secondary hover:text-primary transition-colors duration-200 font-medium font-inter tracking-wide"
                    onClick={(e) => handleNavClick(e, item.href)}
                  >
                    {item.name}
                  </Link>
                ) : (
                  <a
                    key={item.name}
                    href={item.href}
                    className="block px-3 py-2 text-secondary hover:text-primary transition-colors duration-200 font-medium font-inter tracking-wide"
                    onClick={(e) => handleNavClick(e, item.href)}
                  >
                    {item.name}
                  </a>
                )
              ))}
              <a
                href="https://calendly.com/amrit-betterbyai/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="block px-3 py-2 bg-[#FF7066] hover:bg-[#FF8A82] text-white font-medium rounded-md transition-colors text-center mx-3 mt-2"
                onClick={() => setIsOpen(false)}
              >
                Book a Meeting
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;